import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './pages/NotFound';
import { sb } from './supabase';
import {
  DEFAULT_DISPLAY_CURRENCY,
  fetchDisplayRates,
  getCachedDisplayRates,
  normalizeDisplayCurrency,
  persistDisplayCurrencyPreference,
} from './lib/displayCurrency';
import { sendMaabarEmail } from './lib/maabarEmail';
import { fetchMyFactory } from './lib/catalogImport';
import { clearSwrCache } from './lib/useStaleWhileRevalidate';
import {
  getSupplierOnboardingState,
  getSupplierPrimaryRoute,
  getSupplierTradeLinks,
  shouldNotifyAdminOfConfirmedSupplier,
} from './lib/supplierOnboarding';
import {
  AUTH_CALLBACK_PATH,
  buildAuthCallbackPath,
  hasAuthCallbackPayload,
} from './lib/authRedirects';

// Pages
import ComingSoon from './pages/ComingSoon';
import BuyerRegister from './pages/BuyerRegister';
import BuyerWaiting from './pages/BuyerWaiting';
import PreviewAccess from './pages/PreviewAccess';
import Home from './pages/Home';
import ProductDetail from './pages/ProductDetail';
import Login from './pages/Login';
import DashboardBuyer from './pages/DashboardBuyer';
import BuyerBottomNav from './components/buyer/BuyerBottomNav';
import DashboardSupplier from './pages/DashboardSupplier';
import DashboardFactory from './pages/DashboardFactory';
import FactoryThreadEntry from './pages/FactoryThreadEntry';
import FactoryInboxEntry from './pages/FactoryInboxEntry';
import ClaimFactory from './pages/ClaimFactory';
import PublicRfqQuote from './pages/PublicRfqQuote';
import SupplierVerifyDocs from './pages/SupplierVerifyDocs';
import AuthCallback from './pages/AuthCallback';
import About from './pages/About';
import Contact from './pages/Contact';
import Support from './pages/Support';
import Requests from './pages/Requests';
import Factories from './pages/Factories';
import FactoryProducts from './pages/FactoryProducts';
import RequestWizard from './pages/RequestWizard';
import ManagedExplain from './pages/ManagedExplain';
import ManagedOrder from './pages/ManagedOrder';
import FactoryDetail from './pages/FactoryDetail';
import FactoryProductDetail from './pages/FactoryProductDetail';
import MarketProducts from './pages/MarketProducts';
import MarketCollection from './pages/MarketCollection';
import MarketImageSearch from './pages/MarketImageSearch';
import MarketSourcePrepare from './pages/MarketSourcePrepare';
import MarketAddresses from './pages/MarketAddresses';
import MarketHome from './pages/MarketHome';
import MarketFavorites from './pages/MarketFavorites';
import MarketCompare from './pages/MarketCompare';
import MarketProductDetail from './pages/MarketProductDetail';
import MarketPicks from './pages/MarketPicks';
import MarketInquiry from './pages/MarketInquiry';
import MarketInquiries from './pages/MarketInquiries';
import ConsoleMarketInquiries from './pages/admin2/ConsoleMarketInquiries';
import MarketExclusives from './pages/MarketExclusives';
import MarketExclusiveDetail from './pages/MarketExclusiveDetail';
import MarketCart from './pages/MarketCart';
import MarketCheckout from './pages/MarketCheckout';
import MarketOrders from './pages/MarketOrders';
import MarketOrderDetail from './pages/MarketOrderDetail';
import MarketPayMock from './pages/MarketPayMock';
import ConsoleMarketOrders from './pages/admin2/ConsoleMarketOrders';
import ConsoleMarketWeights from './pages/admin2/ConsoleMarketWeights';
import ConsoleMarketSizes from './pages/admin2/ConsoleMarketSizes';
import ConsoleMarketCarrierRates from './pages/admin2/ConsoleMarketCarrierRates';
import ConsoleMarketSafety from './pages/admin2/ConsoleMarketSafety';
import ConsoleMarketStaffShipping from './pages/admin2/ConsoleMarketStaffShipping';
import ConsoleMarketHolds from './pages/admin2/ConsoleMarketHolds';
import ConsoleMarketWaiting from './pages/admin2/ConsoleMarketWaiting';
import ConsoleExclusives from './pages/admin2/ConsoleExclusives';
import ConsoleMarketSettings from './pages/admin2/ConsoleMarketSettings';
import ConsoleMarketProducts from './pages/admin2/ConsoleMarketProducts';
import ConsoleMarketCollections from './pages/admin2/ConsoleMarketCollections';
import ConsoleMarketVariants from './pages/admin2/ConsoleMarketVariants';
import ConsoleMarketIngestion from './pages/admin2/ConsoleMarketIngestion';
import { MARKET_PREVIEW, IMAGE_SEARCH } from './lib/featureFlags';
import LegacyCatalogGate from './components/market/LegacyCatalogGate';
import MarketHomeGate from './components/market/MarketHomeGate';
import { isMarketPath, MarketChromeContext } from './components/market/MarketChrome';
import { keepsLegacyCatalog, useHioBuyOnly } from './lib/marketMode';
import TrackShipment from './pages/TrackShipment';
import RequestDetail from './pages/RequestDetail';
import FactoryRequestView from './pages/FactoryRequestView';
import SupplierProfile from './pages/SupplierProfile';
import Chat from './pages/Chat';
import TelrReturn from './pages/TelrReturn';
import TelrAppReturn from './pages/TelrAppReturn';
import Inbox from './pages/Inbox';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import FAQ from './pages/FAQ';
import FAQTraders from './pages/FAQTraders';
import FAQSuppliers from './pages/FAQSuppliers';
import AdminSeed from './pages/AdminSeed';
import AgentPanel from './pages/AgentPanel';
// ApolloAgent removed — file not found
import PaymentSuccess from './pages/PaymentSuccess';

// Admin dashboard (Phase 1)
import AdminOverview from './pages/admin/AdminOverview';
import ConsoleDashboard from './pages/admin2/ConsoleDashboard';
import ConsoleQuotations from './pages/admin2/ConsoleQuotations';
import ConsoleImport from './pages/admin2/ConsoleImport';
import ConsoleManaged from './pages/admin2/ConsoleManaged';
import ConsoleManagedDetail from './pages/admin2/ConsoleManagedDetail';
import ConsoleRequestFees from './pages/admin2/ConsoleRequestFees';
import ConsoleTemplates from './pages/admin2/ConsoleTemplates';
import ConsoleNotifications from './pages/admin2/ConsoleNotifications';
import ConsoleConversations from './pages/admin2/ConsoleConversations';
import GrowthSection from './pages/growth/GrowthSection';
import GrowthProfit from './pages/growth/GrowthProfit';
import GrowthDashboard from './pages/growth/GrowthDashboard';
import GrowthProducts from './pages/growth/GrowthProducts';
import GrowthJourney from './pages/growth/GrowthJourney';
import GrowthBehavior from './pages/growth/GrowthBehavior';
import GrowthCampaigns from './pages/growth/GrowthCampaigns';
import GrowthSessions from './pages/growth/GrowthSessions';
import GrowthDirector from './pages/growth/GrowthDirector';
import GrowthContext from './pages/growth/GrowthContext';
import GrowthApprovals from './pages/growth/GrowthApprovals';
import GrowthDeals from './pages/growth/GrowthDeals';
import { trackScreen, setTrackingExcluded } from './lib/growthTracker';
import { setClarityInternal } from './lib/clarity';
import ClarityGuard from './components/ClarityGuard';
import ConsoleTraders from './pages/admin2/ConsoleTraders';
import ConsoleTraderDetail from './pages/admin2/ConsoleTraderDetail';
import ConsoleSuppliers from './pages/admin2/ConsoleSuppliers';
import ConsoleFactoryContacts from './pages/admin2/ConsoleFactoryContacts';
import ConsoleClaimCampaign from './pages/admin2/ConsoleClaimCampaign';
import ConsoleLeads from './pages/admin2/ConsoleLeads';
import ConsoleFunnel from './pages/admin2/ConsoleFunnel';
import ConsoleCatalogQA from './pages/admin2/ConsoleCatalogQA';
import ConsoleFeatured from './pages/admin2/ConsoleFeatured';
import ConsoleImageQuality from './pages/admin2/ConsoleImageQuality';
import ConsoleSupplierDetail from './pages/admin2/ConsoleSupplierDetail';
import ConsoleVerification from './pages/admin2/ConsoleVerification';
import ConsoleReviews from './pages/admin2/ConsoleReviews';
import ConsoleContactViolations from './pages/admin2/ConsoleContactViolations';
import ConsoleRequestDetail from './pages/admin2/ConsoleRequestDetail';
import ConsoleDocs from './pages/admin2/ConsoleDocs';
import ConsoleDocNew from './pages/admin2/ConsoleDocNew';
import ConsoleDocEditor from './pages/admin2/ConsoleDocEditor';
import AdminSuppliers from './pages/admin/AdminSuppliers';
import AdminCatalogImport from './pages/admin/AdminCatalogImport';
import AdminCatalogImportDetail from './pages/admin/AdminCatalogImportDetail';
import AdminFactories from './pages/admin/AdminFactories';
import AdminFactoryDetail from './pages/admin/AdminFactoryDetail';
import AdminSupplierDetail from './pages/admin/AdminSupplierDetail';
import AdminReferrals from './pages/admin/AdminReferrals';
import SupplierShareView from './pages/SupplierShareView';
import AdminConcierge from './pages/admin/AdminConcierge';
import AdminConciergeDetail from './pages/admin/AdminConciergeDetail';
import AdminConversations from './pages/admin/AdminConversations';
import AdminConversationDetail from './pages/admin/AdminConversationDetail';
import AdminComingSoon from './pages/admin/AdminComingSoon';
import AdminDisputes from './pages/admin/AdminDisputes';
import AdminDisputeDetail from './pages/admin/AdminDisputeDetail';
import AdminTraders from './pages/admin/AdminTraders';
import AdminTraderDetail from './pages/admin/AdminTraderDetail';
import AdminOrders from './pages/admin/AdminOrders';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminPayments from './pages/admin/AdminPayments';
import AdminSupport from './pages/admin/AdminSupport';
import AdminTicketDetail from './pages/admin/AdminTicketDetail';
import AdminEmails from './pages/admin/AdminEmails';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminSettings from './pages/admin/AdminSettings';

// Components
import Navbar from './components/Navbar';
import AIHub from './components/AIHub';
import HelpButton from './components/HelpButton';
import MarketBottomNav from './components/market/MarketBottomNav';
import BrandedLoading from './components/BrandedLoading';
import useMobileViewport from './hooks/useMobileViewport';

// Traders and visitors see SAR ONLY (owner, 2026-09-29): the exchange rate is
// internal, and a USD figure next to a SAR one reveals it. Only a SUPPLIER
// keeps a display-currency preference, for reading SAR amounts (a trader's
// budget, a target price) in their own currency. Everyone else — anonymous
// visitors in any language, traders, admins browsing the storefront — is SAR.
function resolveDisplayCurrency(profile, lang) {
  if (String(profile?.role || '').toLowerCase() !== 'supplier') return 'SAR';
  if (profile?.preferred_display_currency) return normalizeDisplayCurrency(profile.preferred_display_currency);
  return lang === 'ar' ? 'SAR' : 'USD';
}

function SupplierVerificationLocked({ lang }) {
  return (
    <div style={{
      minHeight: 'var(--app-dvh)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-base)',
      padding: 24,
      color: 'var(--text-primary)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 560,
        borderRadius: 24,
        border: '1px solid var(--border-subtle)',
        background: '#FFFFFF',
        padding: '36px 30px',
        textAlign: lang === 'ar' ? 'right' : 'left',
      }}>
        <p style={{
          fontSize: 11,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: 'var(--text-tertiary)',
          margin: '0 0 12px',
        }}>
          MAABAR SUPPLIER ACCESS
        </p>
        <h1 style={{
          margin: '0 0 12px',
          fontSize: lang === 'ar' ? 30 : 32,
          fontWeight: 300,
          lineHeight: 1.15,
          fontFamily: lang === 'ar' ? 'var(--font-ar)' : lang === 'zh' ? 'var(--font-sans)' : 'var(--font-sans)',
        }}>
          {lang === 'zh' ? '请先完成认证以解锁完整供应商体验' : lang === 'ar' ? 'أكمل التحقق لفتح تجربة المورد الكاملة على مَعبر' : 'Complete verification to unlock the full supplier experience on Maabar'}
        </h1>
        <p style={{
          margin: 0,
          fontSize: 14,
          lineHeight: 1.8,
          color: 'var(--text-secondary)',
          fontFamily: lang === 'ar' ? 'var(--font-ar)' : 'var(--font-sans)',
        }}>
          {lang === 'ar'
            ? 'يمكنك استخدام لوحة المورد، إعدادات الملف، ومسار التحقق فقط إلى أن يتم توثيق الحساب.'
            : lang === 'zh'
              ? '在账户完成验证前，您只能使用供应商控制台、资料设置和认证流程。'
              : 'Until your account is verified, only the supplier dashboard, profile settings, and verification flow stay open.'}
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 22 }}>
          <button
            onClick={() => window.location.assign('/dashboard')}
            style={{
              background: 'var(--text-primary)',
              color: 'var(--bg-base)',
              border: 'none',
              borderRadius: 14,
              minHeight: 44,
              padding: '11px 18px',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {lang === 'ar' ? 'العودة إلى لوحة المورد' : lang === 'zh' ? '返回供应商控制台' : 'Back to supplier dashboard'}
          </button>
          <button
            onClick={() => window.location.assign('/dashboard?tab=verification')}
            style={{
              background: 'transparent',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border-default)',
              borderRadius: 14,
              minHeight: 44,
              padding: '11px 18px',
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            {lang === 'ar' ? 'افتح التحقق' : lang === 'zh' ? '打开认证流程' : 'Open verification'}
          </button>
        </div>
      </div>
    </div>
  );
}

// A supplier account may be a claimed factory (role='supplier' + linked_supplier_id
// on a factory_directory row). Detect that on entry and route to the factory
// dashboard; otherwise fall through to the normal supplier onboarding stages.
function SupplierHome({ user, profile, isPreview, sharedProps }) {
  const { lang } = sharedProps;
  const [checking, setChecking] = useState(true);
  const [factory, setFactory] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const f = await fetchMyFactory(); if (alive) setFactory(f || null); }
      catch { /* not a linked factory — fall through */ }
      if (alive) setChecking(false);
    })();
    return () => { alive = false; };
  }, []);

  if (checking) return <BrandedLoading lang={lang} tone="dashboard" fullscreen />;
  // A claimed factory owner sees the dedicated factory dashboard ONCE, as a first
  // step (review the catalog/prices), then Skip/Continue takes them to the normal
  // supplier dashboard like any supplier. The step is marked done on the account
  // (profiles.onboarding_completed) so later logins go straight to DashboardSupplier;
  // the factory catalog stays reachable at /dashboard/factory.
  if (factory && profile?.onboarding_completed !== true) {
    return <DashboardFactory factory={factory} firstStep {...sharedProps} />;
  }
  // (factory && onboarding_completed) falls through to DashboardSupplier below.

  const supplierState = getSupplierOnboardingState(profile, user);
  const centered = {
    minHeight: 'var(--app-dvh)', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: 'var(--bg-base)', gap: 16, padding: 24, textAlign: 'center',
  };
  const signOutBtn = (
    <button onClick={() => sb.auth.signOut()} style={{
      background: 'none', color: 'var(--text-disabled)',
      border: '1px solid var(--border-default)', padding: '10px 24px',
      borderRadius: 6, fontSize: 12, cursor: 'pointer', marginTop: 8,
    }}>
      {lang === 'zh' ? '退出登录' : lang === 'en' ? 'Sign Out' : 'تسجيل الخروج'}
    </button>
  );

  if (!isPreview && supplierState.isRejectedStage)
    return (
      <div style={centered}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 16, fontFamily: lang === 'ar' ? 'var(--font-ar)' : 'var(--font-sans)', lineHeight: 1.8 }}>
          {lang === 'zh' ? '很遗憾，您的账户申请未能通过。' : lang === 'en' ? 'Unfortunately, your supplier account was not approved.' : 'نأسف، لم يتم قبول حسابك.'}
        </p>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 13, fontFamily: lang === 'ar' ? 'var(--font-ar)' : 'var(--font-sans)' }}>
          {lang === 'zh' ? '如有疑问，请联系我们：' : lang === 'en' ? 'For inquiries, contact us at: ' : 'للاستفسار تواصل معنا على '}
          <a href="mailto:hello@maabar.io" style={{ color: 'var(--text-secondary)' }}>hello@maabar.io</a>
        </p>
        {signOutBtn}
      </div>
    );

  if (!isPreview && supplierState.isInactiveStage)
    return (
      <div style={centered}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 16, fontFamily: lang === 'ar' ? 'var(--font-ar)' : 'var(--font-sans)', lineHeight: 1.8 }}>
          {lang === 'zh' ? '供应商账户已被暂停。' : lang === 'en' ? 'Your supplier account has been temporarily suspended.' : 'حساب المورد متوقف مؤقتاً.'}
        </p>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 13, fontFamily: lang === 'ar' ? 'var(--font-ar)' : 'var(--font-sans)', maxWidth: 460, lineHeight: 1.9 }}>
          {lang === 'zh' ? '如需说明或重新激活，请通过您注册时使用的邮箱联系我们：' : lang === 'en' ? 'For clarification or reactivation, contact us at: ' : 'إذا كنت تحتاج توضيحاً أو إعادة تفعيل، تواصل معنا على '}
          <a href="mailto:hello@maabar.io" style={{ color: 'var(--text-secondary)' }}>hello@maabar.io</a>
        </p>
        {signOutBtn}
      </div>
    );

  return <DashboardSupplier {...sharedProps} />;
}

function DashboardRouter({ loading, user, profile, profileError, setProfileError, setLoading, loadProfile, sharedProps }) {
  const { lang } = sharedProps;
  const location = useLocation();
  if (loading || (user && !profile && !profileError)) {
    return <BrandedLoading lang={lang} tone="dashboard" fullscreen />;
  }
  // Signed out. This used to fall through to `if (!profile)` below and render
  // DashboardBuyer with user = null, which reads user.id while rendering and
  // threw before its own redirect effect could run — the whole page became
  // "Something went wrong". Every email CTA points at /dashboard, so anyone
  // opening one in a signed-out browser hit it (a supplier, 2026-10-09).
  // zh → the supplier door: our Chinese-language visitors are suppliers.
  if (!user) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`${lang === 'zh' ? '/login/supplier' : '/login'}?next=${next}`} replace />;
  }
  if (profileError) return (
    <div style={{
      minHeight: 'var(--app-dvh)', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-base)', gap: 16, padding: 24,
    }}>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, fontFamily: lang === 'ar' ? 'var(--font-ar)' : 'var(--font-sans)', textAlign: 'center', lineHeight: 1.8 }}>
        {lang === 'zh' ? '账户数据加载失败，请检查网络后重试。' : lang === 'en' ? 'Failed to load account data. Check your connection and try again.' : 'فشل تحميل بيانات الحساب. تحقق من اتصالك وحاول مجدداً.'}
      </p>
      <button
        onClick={() => { setProfileError(false); setLoading(true); loadProfile(user?.id, 1, user); }}
        style={{
          background: '#1a1a1a', color: '#ffffff',
          border: 'none', padding: '12px 28px', borderRadius: 6,
          fontSize: 13, cursor: 'pointer', fontWeight: 500,
        }}>
        {lang === 'zh' ? '重试' : lang === 'en' ? 'Retry' : 'إعادة المحاولة'}
      </button>
      <button
        onClick={() => sb.auth.signOut()}
        style={{
          background: 'none', color: 'var(--text-disabled)',
          border: '1px solid var(--border-default)', padding: '10px 24px', borderRadius: 6,
          fontSize: 12, cursor: 'pointer',
        }}>
        {lang === 'zh' ? '退出登录' : lang === 'en' ? 'Sign Out' : 'تسجيل الخروج'}
      </button>
    </div>
  );
  if (!profile) return <DashboardBuyer {...sharedProps} />;
  const isPreview = localStorage.getItem('maabar_preview') === '1';
  if (profile.role === 'admin' || profile.role === 'super_admin') return <Navigate to="/admin2/dashboard" replace />;
  if (profile.role === 'buyer') {
    const LAUNCH_DATE = new Date('2026-05-01T00:00:00+03:00');
    if (new Date() < LAUNCH_DATE && !isPreview) return <BuyerWaiting {...sharedProps} />;
    return <DashboardBuyer {...sharedProps} />;
  }
  if (profile.role === 'supplier')
    return <SupplierHome user={user} profile={profile} isPreview={isPreview} sharedProps={sharedProps} />;
  return <DashboardBuyer {...sharedProps} />;
}

function AppContent({ lang, profile, user, sharedProps, loading, profileError, setProfileError, setLoading, loadProfile }) {
  const location = useLocation();
  // Growth OS web tracker: one screen_viewed per route change; the team's own
  // accounts (admin / super_admin) are not counted as trader behaviour.
  useEffect(() => { trackScreen(location.pathname); }, [location.pathname]);
  useEffect(() => {
    const team = ['admin', 'super_admin'].includes(profile?.role);
    setTrackingExcluded(team);
    setClarityInternal(team);   // Clarity tag internal=true on public pages
  }, [profile?.role]);
  const isAuthCallbackPage = location.pathname === AUTH_CALLBACK_PATH;
  const isAdminPage = location.pathname.startsWith('/admin');
  // Share links are opened by people outside Maabar (a partner verifying a
  // supplier); the dossier stands alone, with no site nav or AI hub around it.
  const isSharePage = location.pathname.startsWith('/s/');
  const isChromelessPage = isAuthCallbackPage
    || isAdminPage
    || isSharePage
    || location.pathname === '/buyer'
    || location.pathname === '/preview'
    || location.pathname === '/login'
    || location.pathname.startsWith('/login/')
    || location.pathname.startsWith('/factory-chat/')
    || location.pathname.startsWith('/claim/')
    || location.pathname.startsWith('/messages/factory/')
    || location.pathname.startsWith('/factory/thread/')
    || location.pathname.startsWith('/factory/inbox/')
    || location.pathname.startsWith('/market/inquiries/');
  const isSupplierAccessPage = location.pathname === '/supplier-access';
  const isLTRPage = isAuthCallbackPage || isSupplierAccessPage;
  const pageDir = isLTRPage ? 'ltr' : (lang === 'ar' ? 'rtl' : 'ltr');

  // <html lang> follows the UI language. It was hard-coded `en` in index.html
  // and never updated, because direction is applied to the wrapper div below
  // rather than to the document element.
  //
  // The Saudi Business Center seal reads exactly this and nothing else —
  // `document.documentElement.getAttribute('lang')` in seal.js — and passes it
  // to the iframe as `?lang=`.
  //
  // ⚠️ CORRECTED 2026-09-21. This comment used to go on to say the seal "would
  // render in English on the Arabic site" if <html lang> were left at `en`. It
  // would not: **the seal is Arabic-only and ignores `?lang=` entirely.**
  // Measured with our real token at lang=ar/en/zh/fr — all four return the same
  // 8730 bytes of `<html lang="ar" dir="rtl">`. So nothing below changes what
  // the seal displays, today. The wrong half is struck rather than the whole
  // comment deleted, because the seal IS still the reason this effect was
  // written and will be the reason it matters if SBC ever ships a second
  // language. Full measurement in src/components/Footer.jsx and
  // docs/TECH_DEBT.md.
  //
  // Footer.jsx makes the same assignment immediately before it appends seal.js.
  // That is not a duplicate to be tidied away: React flushes child effects
  // before parent ones, so this effect runs AFTER the footer's and cannot be
  // what the first mount reads.
  //
  // It is also simply correct: screen readers, translation prompts and
  // hyphenation all key off this, and every one of them has been told the page
  // is English since the app was built.
  useEffect(() => {
    document.documentElement.lang = lang || 'ar';
  }, [lang]);
  // Persistent trader bottom-nav shell — a logged-in buyer gets the fixed mobile
  // bottom nav across all their pages (dashboard, products, factories, managed order).
  const isBuyerShell = !!user && profile?.role === 'buyer' && !isChromelessPage;
  // One header on market pages: the marketplace bar (MarketShell) replaces the
  // site navbar — on /market/*, and on "/" once it shows the marketplace.
  const marketMode = useHioBuyOnly();
  const marketChrome = MARKET_PREVIEW && (isMarketPath(location.pathname)
    || (location.pathname === '/' && marketMode.ready && marketMode.hioBuyOnly && !keepsLegacyCatalog(profile)));
  // Before paint: the offsets fixed elements read (--page-top-offset) must be
  // the market header's on the FIRST frame, or the WhatsApp button flashes over it.
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('mk-chrome', !!marketChrome);
  }, [marketChrome]);
  // The supplier dashboard draws its own header (SupplierHeader) and the site
  // navbar is hidden there (below), but the page still reserved the navbar's
  // height: a blank strip above the header, and the WhatsApp button — placed
  // at --page-top-offset — landed ON the header's account button, so the
  // account menu could not be opened on desktop. index.css html.sup-chrome.
  const supplierChrome = profile?.role === 'supplier' && location.pathname === '/dashboard' && !isChromelessPage;
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('sup-chrome', !!supplierChrome);
  }, [supplierChrome]);
  // The trader's bottom bar stays on store pages too (owner, 2026-10-21: «كيف يرجع
  // للوحتي… المفروض يكون تحت وثابت»), except while paying: checkout is one
  // task on its own screen (Apple HIG), with its own way back to the cart.
  const showBuyerBar = isBuyerShell && !marketChrome;
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('buyer-bar', !!showBuyerBar);
  }, [showBuyerBar]);
  const supplierState = profile?.role === 'supplier' ? getSupplierOnboardingState(profile, user) : null;
  const supplierPrimaryRoute = profile?.role === 'supplier' ? getSupplierPrimaryRoute(profile, user) : '/dashboard';

  if (!isAuthCallbackPage && hasAuthCallbackPayload(location.search, location.hash)) {
    return <Navigate to={buildAuthCallbackPath(location.search, location.hash)} replace />;
  }

  // Verification no longer gates operational routes — a registered supplier can
  // browse requests, message, and use their inbox immediately. Each route gates on
  // the specific capability (all true pre-verification for an active supplier);
  // only Payout/Wallet remain verified-only, and those live inside the dashboard.
  const withSupplierCapability = (capable, element) => {
    if (supplierState && !capable) {
      return <SupplierVerificationLocked lang={lang} />;
    }
    return element;
  };

  return (
    <div dir={pageDir} className={`app-shell${showBuyerBar ? ' buyer-shell' : ''}`}>
      {/* The supplier dashboard renders its own header (SupplierHeader), so the global Navbar is hidden there. */}
      {/* Market pages (and "/" when it IS the market) carry MAABAR's one header —
          the marketplace bar with the logo, account and services — so the site
          navbar is not drawn twice there. */}
      {!isChromelessPage && !marketChrome && !supplierChrome && <Navbar {...sharedProps} logoOnly={isSupplierAccessPage} />}
      <MarketChromeContext.Provider value={sharedProps}>
      <Routes>
        {/* Homepage temporarily reverted to <Home> for Moyasar review.
            To re-enable countdown after approval: swap element back to <ComingSoon />
            and re-add `location.pathname === '/'` to isChromelessPage above.
            See MAABAR_CURRENCY_STATUS.md → "Countdown re-enable". */}
        {/* "/" is the marketplace for customers once marketplace_public is on (MarketHomeGate). */}
        <Route path="/"               element={<MarketHomeGate profile={profile} legacy={<Home {...sharedProps} />} market={MARKET_PREVIEW ? <MarketHome {...sharedProps} /> : null} />} />
        <Route path="/buyer"          element={<BuyerRegister   user={user} />} />
        <Route path="/preview"        element={<PreviewAccess   {...sharedProps} />} />
        {/* Request-first: the browse directories are replaced by Factories.
            Detail pages (/products/:id, /supplier/:id) stay — used by chat,
            managed shortlists, invites. These redirects guarantee no browse
            directory survives, regardless of any lingering links to them. */}
        {/* LegacyCatalogGate: in HioBuy-only mode (lib/marketMode.js) customers
            are sent to /market; suppliers and admins keep these pages. */}
        <Route path="/products"       element={<LegacyCatalogGate profile={profile}><FactoryProducts {...sharedProps} /></LegacyCatalogGate>} />
        <Route path="/products/:id"   element={<LegacyCatalogGate profile={profile}><ProductDetail   {...sharedProps} /></LegacyCatalogGate>} />
        {/* HioBuy marketplace — routed only in a MARKET_PREVIEW build; what customers
            see inside is decided by market_settings.marketplace_public (lib/marketMode.js). */}
        {MARKET_PREVIEW && <Route path="/market"     element={<MarketHome          {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/search"     element={<MarketProducts {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/c/:cat"     element={<MarketProducts {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/c/:cat/:sub" element={<MarketProducts {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/c/:cat/:sub/:type" element={<MarketProducts {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/products"   element={<MarketProducts {...sharedProps} />} />}
        {/* The earlier "all categories" page: browsing is now the all-products page. */}
        {MARKET_PREVIEW && <Route path="/market/categories" element={<Navigate to="/market/products" replace />} />}
        {MARKET_PREVIEW && <Route path="/market/addresses" element={<MarketAddresses {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/collection/:slug" element={<MarketCollection {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/exclusives" element={<MarketExclusives {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/exclusives/:id" element={<MarketExclusiveDetail {...sharedProps} />} />}
        {IMAGE_SEARCH && <Route path="/market/image-search" element={<MarketImageSearch {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/prepare/:ref" element={<MarketSourcePrepare {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/favorites"  element={<MarketFavorites {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/compare"    element={<MarketCompare  {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/cart"     element={<MarketCart     {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/checkout" element={<MarketCheckout {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/orders"     element={<MarketOrders      {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/orders/:id" element={<MarketOrderDetail {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/pay/mock"   element={<MarketPayMock />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-orders"   element={<ConsoleMarketOrders   {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-weights"  element={<ConsoleMarketWeights  {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-sizes"    element={<ConsoleMarketSizes    {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-carrier-rates" element={<ConsoleMarketCarrierRates {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-safety"   element={<ConsoleMarketSafety   {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-staff-shipping" element={<ConsoleMarketStaffShipping {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-holds"    element={<ConsoleMarketHolds    {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-waiting"  element={<ConsoleMarketWaiting  {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-inquiries" element={<ConsoleMarketInquiries {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-exclusives" element={<ConsoleExclusives {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-settings" element={<ConsoleMarketSettings {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-products" element={<ConsoleMarketProducts {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-collections" element={<ConsoleMarketCollections {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-variants" element={<ConsoleMarketVariants {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/admin2/market-ingestion" element={<ConsoleMarketIngestion {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/inquiries" element={<MarketInquiries {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/inquiries/:id" element={<MarketInquiry {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/picks/:tab" element={<MarketPicks {...sharedProps} />} />}
        {MARKET_PREVIEW && <Route path="/market/:id" element={<MarketProductDetail {...sharedProps} />} />}
        <Route path="/login/:role"    element={<Login           {...sharedProps} />} />
        <Route path="/login"          element={<Login           {...sharedProps} />} />
        <Route path="/auth/callback"  element={<AuthCallback    {...sharedProps} />} />
        <Route path="/dashboard"      element={<DashboardRouter loading={loading} user={user} profile={profile} profileError={profileError} setProfileError={setProfileError} setLoading={setLoading} loadProfile={loadProfile} sharedProps={sharedProps} />} />
        <Route path="/dashboard/factory" element={<DashboardFactory revisit {...sharedProps} />} />
        <Route path="/about"          element={<About           {...sharedProps} />} />
        <Route path="/contact"        element={<Contact         {...sharedProps} />} />
        <Route path="/support"        element={<Support         {...sharedProps} />} />
        <Route path="/managed"        element={<ManagedExplain  {...sharedProps} />} />
        <Route path="/managed-order/:id" element={<ManagedOrder {...sharedProps} />} />
        {/* WhatsApp button targets. /track/:token is PUBLIC and deliberately
            outside any auth wrapper — it reveals nothing and redirects to the
            carrier. /requests/:id is login-gated and relies on RLS, not on a
            wrapper, so a supplier with standing on the request can open it too. */}
        <Route path="/track/:token"   element={<TrackShipment lang={lang} />} />
        <Route path="/requests/:id"   element={<RequestDetail {...sharedProps} />} />
        <Route path="/request"        element={<RequestWizard   {...sharedProps} />} />
        {/* NOT wrapped in withSupplierCapability: that guard is
            `if (supplierState && !capable) -> locked`, and supplierState is
            null for a buyer, so it never guarded this route for the four
            BUYER entry points that land here (BuyerBottomNav, DashboardBuyer
            x3). Since 2026-09-19 this page is the trader's create form only —
            the supplier browse half is gone — and Requests.jsx itself sends a
            supplier to their dashboard. */}
        <Route path="/requests"       element={<Requests        {...sharedProps} />} />
        <Route path="/factories"          element={<LegacyCatalogGate profile={profile}><Factories       {...sharedProps} /></LegacyCatalogGate>} />
        <Route path="/factories/:key"     element={<LegacyCatalogGate profile={profile}><Factories       {...sharedProps} /></LegacyCatalogGate>} />
        <Route path="/factory/:id"        element={<LegacyCatalogGate profile={profile}><FactoryDetail   {...sharedProps} /></LegacyCatalogGate>} />
        <Route path="/factory/:factoryId/product/:productId" element={<LegacyCatalogGate profile={profile}><FactoryProductDetail {...sharedProps} /></LegacyCatalogGate>} />
        <Route path="/f/:slug"            element={<FactoryRequestView />} />
        <Route path="/factory-chat/:slug" element={<FactoryThreadEntry {...sharedProps} />} />
        {/* WhatsApp template 20 entry — resolves and redirects, never lands on /dashboard except as the owner fallback. */}
        <Route path="/factory/inbox/:slug" element={<FactoryInboxEntry {...sharedProps} />} />
        <Route path="/claim/:slug"        element={<ClaimFactory onLangChange={sharedProps.setLang} />} />
        {/* Public RFQ + quote form, from a link posted to Facebook/WhatsApp/TikTok.
            No auth to read; quoting routes through ?next= back to this same url,
            which is what keeps registration and quoting in one session. */}
        <Route path="/rfq/:token/quote"   element={<PublicRfqQuote lang={lang} />} />
        {/* Stage 1 of the two-bar verification model — the visibility bar.
            Where a supplier lands straight after submitting a public quote. */}
        <Route path="/supplier/verify"   element={<SupplierVerifyDocs lang={lang} />} />
        {/* /messages was a second, legacy-only factory inbox. /inbox already shows
            the same conversations on the unified stack alongside direct DMs, so
            this redirects rather than maintaining two. */}
        <Route path="/messages"           element={<Navigate to="/inbox" replace />} />
        <Route path="/supplier"       element={<Navigate to="/login/supplier" replace />} />
        <Route path="/supplier-access" element={<Navigate to="/login/supplier" replace />} />
        <Route path="/supplier/:id"   element={<SupplierProfile {...sharedProps} />} />
        <Route path="/suppliers"      element={<Navigate to="/factories" replace />} />
        <Route path="/chat/f/:factoryId" element={withSupplierCapability(supplierState?.canAccessMessaging, <Chat {...sharedProps} />)} />
        <Route path="/chat/f/:factoryId/:traderId" element={withSupplierCapability(supplierState?.canAccessMessaging, <Chat {...sharedProps} />)} />
        <Route path="/chat/:partnerId"element={withSupplierCapability(supplierState?.canAccessMessaging, <Chat            {...sharedProps} />)} />
        <Route path="/inbox"          element={withSupplierCapability(supplierState?.canAccessMessaging, <Inbox           {...sharedProps} />)} />
        <Route path="/telr-return"    element={<TelrReturn      {...sharedProps} />} />
        {/* APP payments: Telr returns here (https), and the page hands the same
            query string to maabar://payment-return. Public, verifies nothing. */}
        <Route path="/telr-app-return" element={<TelrAppReturn  {...sharedProps} />} />
        <Route path="/terms"          element={<Terms           {...sharedProps} />} />
        <Route path="/privacy"        element={<Privacy         {...sharedProps} />} />
        <Route path="/faq"            element={<FAQ             {...sharedProps} />} />
        <Route path="/faq/traders"    element={<FAQTraders      {...sharedProps} />} />
        <Route path="/faq/suppliers"  element={<FAQSuppliers    {...sharedProps} />} />
        <Route path="/admin-seed"     element={<AdminSeed       {...sharedProps} />} />
        <Route path="/agent"          element={<AgentPanel />} />

        {/* Admin Console v2 (parallel build — factory_directory-centric) */}
        <Route path="/admin2"                  element={<Navigate to="/admin2/dashboard" replace />} />
        <Route path="/admin2/dashboard"        element={<ConsoleDashboard     {...sharedProps} />} />
        {/* Growth OS — its own full-page layout, owner-only on every route (GrowthShell). */}
        <Route path="/admin2/growth"           element={<GrowthDashboard      {...sharedProps} />} />
        <Route path="/admin2/growth/products"  element={<GrowthProducts       {...sharedProps} />} />
        <Route path="/admin2/growth/journey"   element={<GrowthJourney        {...sharedProps} />} />
        <Route path="/admin2/growth/behavior"  element={<GrowthBehavior       {...sharedProps} />} />
        <Route path="/admin2/growth/campaigns" element={<GrowthCampaigns      {...sharedProps} />} />
        <Route path="/admin2/growth/sessions"  element={<GrowthSessions       {...sharedProps} />} />
        <Route path="/admin2/growth/profitability" element={<GrowthProfit     {...sharedProps} />} />
        <Route path="/admin2/growth/ai-manager" element={<GrowthDirector  {...sharedProps} />} />
        <Route path="/admin2/growth/context"   element={<GrowthContext       {...sharedProps} />} />
        <Route path="/admin2/growth/deals"     element={<GrowthDeals         {...sharedProps} />} />
        <Route path="/admin2/growth/approvals" element={<GrowthApprovals     {...sharedProps} />} />
        <Route path="/admin2/growth/:section"  element={<GrowthSection        {...sharedProps} />} />
        <Route path="/admin2/managed"          element={<ConsoleManaged       {...sharedProps} />} />
        <Route path="/admin2/managed/:id"      element={<ConsoleManagedDetail {...sharedProps} />} />
        <Route path="/admin2/request-fees"     element={<ConsoleRequestFees   {...sharedProps} />} />
        <Route path="/admin2/docs"             element={<ConsoleDocs          {...sharedProps} />} />
        <Route path="/admin2/docs/new"         element={<ConsoleDocNew        {...sharedProps} />} />
        <Route path="/admin2/docs/:id"         element={<ConsoleDocEditor     {...sharedProps} />} />
        <Route path="/admin2/suppliers"        element={<ConsoleSuppliers     {...sharedProps} />} />
        <Route path="/admin2/factory-contacts" element={<ConsoleFactoryContacts {...sharedProps} />} />
        <Route path="/admin2/claim-campaign"   element={<ConsoleClaimCampaign {...sharedProps} />} />
        <Route path="/admin2/leads"            element={<ConsoleLeads         {...sharedProps} />} />
        <Route path="/admin2/funnel"           element={<ConsoleFunnel        {...sharedProps} />} />
        <Route path="/admin2/catalog-qa"       element={<ConsoleCatalogQA     {...sharedProps} />} />
        <Route path="/admin2/featured"         element={<ConsoleFeatured      {...sharedProps} />} />
        <Route path="/admin2/image-quality"    element={<ConsoleImageQuality  {...sharedProps} />} />
        <Route path="/admin2/suppliers/:id"    element={<ConsoleSupplierDetail {...sharedProps} />} />
        {/* The verification LIST merged into the unified page; the review FILE did
            not move, so a link an admin saved to a specific supplier still opens
            exactly what it opened before. */}
        <Route path="/admin2/verification"     element={<Navigate to="/admin2/suppliers?tab=registered" replace />} />
        <Route path="/admin2/verification/:id" element={<ConsoleVerification  {...sharedProps} />} />
        <Route path="/admin2/reviews"          element={<ConsoleReviews       {...sharedProps} />} />
        <Route path="/admin2/contact-violations" element={<ConsoleContactViolations {...sharedProps} />} />
        {/* A non-managed request had no detail screen at all: the board could
            list one and distribute it, never open it. */}
        <Route path="/admin2/requests/:id"     element={<ConsoleRequestDetail {...sharedProps} />} />
        <Route path="/admin2/quotations"       element={<ConsoleQuotations    {...sharedProps} />} />
        <Route path="/admin2/import"           element={<ConsoleImport        {...sharedProps} />} />
        <Route path="/admin2/templates"        element={<ConsoleTemplates     {...sharedProps} />} />
        <Route path="/admin2/notifications"    element={<ConsoleNotifications {...sharedProps} />} />
        <Route path="/admin2/conversations"    element={<ConsoleConversations {...sharedProps} />} />
        <Route path="/admin2/traders"          element={<ConsoleTraders       {...sharedProps} />} />
        <Route path="/admin2/traders/:id"      element={<ConsoleTraderDetail  {...sharedProps} />} />

        {/* Admin dashboard */}
        <Route path="/admin"                   element={<Navigate to="/admin2/dashboard" replace />} />
        <Route path="/admin/overview"          element={<AdminOverview        {...sharedProps} />} />
        <Route path="/admin/suppliers"         element={<AdminSuppliers       {...sharedProps} />} />
        <Route path="/admin/suppliers/:id"     element={<AdminSupplierDetail  {...sharedProps} />} />
        <Route path="/admin/catalog-import"    element={<AdminCatalogImport   {...sharedProps} />} />
        <Route path="/admin/catalog-import/:id" element={<AdminCatalogImportDetail {...sharedProps} />} />
        <Route path="/admin/factories"         element={<AdminFactories       {...sharedProps} />} />
        <Route path="/admin/factories/:id"     element={<AdminFactoryDetail   {...sharedProps} />} />
        <Route path="/admin/referrals"         element={<AdminReferrals       {...sharedProps} />} />
        <Route path="/s/supplier/:token"       element={<SupplierShareView    lang={lang} />} />
        <Route path="/admin/concierge"         element={<AdminConcierge       {...sharedProps} />} />
        <Route path="/admin/concierge/:id"     element={<AdminConciergeDetail {...sharedProps} />} />
        <Route path="/admin/conversations"     element={<AdminConversations   {...sharedProps} />} />
        <Route path="/admin/conversations/:threadId" element={<AdminConversationDetail {...sharedProps} />} />
        <Route path="/admin/disputes"          element={<AdminDisputes        {...sharedProps} />} />
        <Route path="/admin/disputes/:id"      element={<AdminDisputeDetail   {...sharedProps} />} />
        <Route path="/admin/traders"           element={<AdminTraders         {...sharedProps} />} />
        <Route path="/admin/traders/:id"       element={<AdminTraderDetail    {...sharedProps} />} />
        <Route path="/admin/orders"            element={<AdminOrders          {...sharedProps} />} />
        <Route path="/admin/orders/:id"        element={<AdminOrderDetail     {...sharedProps} />} />
        <Route path="/admin/payments"          element={<AdminPayments        {...sharedProps} />} />
        <Route path="/admin/support"           element={<AdminSupport         {...sharedProps} />} />
        <Route path="/admin/support/:id"       element={<AdminTicketDetail    {...sharedProps} />} />
        <Route path="/admin/emails"            element={<AdminEmails          {...sharedProps} />} />
        <Route path="/admin/analytics"         element={<AdminAnalytics       {...sharedProps} />} />
        <Route path="/admin/settings"          element={<AdminSettings        {...sharedProps} />} />
        {/* /apollo route removed — ApolloAgent not available */}
        {/* Moyasar checkout retired 2026-09-26 — Telr is the only gateway. Old
            links and the Moyasar return page land on the dashboard instead. */}
        <Route path="/checkout"       element={<Navigate to="/dashboard" replace />} />
        <Route path="/payment-success"element={<PaymentSuccess  {...sharedProps} />} />
        <Route path="*"               element={<NotFound        {...sharedProps} />} />
      </Routes>
      </MarketChromeContext.Provider>

      {/* The AI tools' panels; their floating button is the one «تحتاج مساعدة؟» below. */}
      {!isChromelessPage && (!user || profile?.role === 'buyer') && (
        <AIHub lang={lang} user={user} profile={profile} setProfile={sharedProps.setProfile} noFab />
      )}
      {/* ONE help button site-wide (owner, 2026-11-02): the assistant tools + WhatsApp.
          It replaces AIHub's «AI» button and the WhatsApp button (components kept). */}
      {!isChromelessPage && <HelpButton lang={lang} showTools={!user || profile?.role === 'buyer'} />}
      {/* Market pages: their own bottom nav for everyone (owner, 2026-11-02); the
          trader bottom nav elsewhere — never two at once. */}
      {marketChrome ? <MarketBottomNav lang={lang} user={user} home={marketMode.hioBuyOnly && !keepsLegacyCatalog(profile) ? '/' : '/market'} />
        : showBuyerBar && <BuyerBottomNav lang={lang} />}
    </div>
  );
}

// Live supplier status refresh (see the effect in App). Only the fields an
// admin's verification decision writes are merged into the in-memory profile.
const PROFILE_REFRESH_THROTTLE_MS = 15 * 1000;
const PROFILE_POLL_MS = 60 * 1000;
const LIVE_PROFILE_KEYS = ['status', 'verification_review_note'];

function App() {
  useMobileViewport();

  const [user, setUser] = useState(null);
  // Tracks the logged-in user id across the auth listener closure, so we can tell
  // a genuinely new login from Supabase re-firing SIGNED_IN on tab focus.
  const currentUserIdRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem('maabar_lang');
    if (stored && ['ar', 'en', 'zh'].includes(stored)) return stored;
    // First visit (no saved choice): mirror the mobile app — use the browser
    // locale when it's one of our three languages. zh-CN / zh-TW / zh-Hans all
    // slice to 'zh', so a Chinese factory opening a shared link gets a Chinese
    // page without touching the toggle, which is the whole point.
    const nav = (navigator.language || (navigator.languages && navigator.languages[0]) || '').slice(0, 2).toLowerCase();
    if (['ar', 'en', 'zh'].includes(nav)) return nav;

    // UNMATCHED LOCALE. The fallback depends on who the page is FOR, because
    // the wrong guess is expensive in only one direction.
    //
    // On the trader-facing site Arabic is right: the audience is Saudi, and a
    // visitor with an unusual browser locale is far more likely to read Arabic
    // than not.
    //
    // On the SUPPLIER-facing public pages it is wrong. Those links are shared
    // into Facebook groups, WhatsApp and TikTok and reach factories well
    // beyond mainland China — Vietnamese, Korean, Turkish, Russian locales all
    // landed on an Arabic page. A supplier who cannot read the page also
    // cannot find the toggle that would fix it; they close the tab, and we
    // never hear about it. English is the lingua franca of that trade, so it
    // is the safe default there.
    const path = (typeof window !== 'undefined' && window.location?.pathname) || '';
    const supplierFacing = path.startsWith('/rfq/') || path.startsWith('/f/') || path.startsWith('/supplier/verify');
    return supplierFacing ? 'en' : 'ar';
  });
  const [displayCurrency, setDisplayCurrency] = useState('SAR');
  const [exchangeRates, setExchangeRates] = useState(() => getCachedDisplayRates());
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  const welcomeEmailSentRef = React.useRef(false);
  const supplierAdminEmailSentRef = React.useRef(new Set());

  useEffect(() => {
    fetchDisplayRates(sb).then(setExchangeRates);

    sb.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        currentUserIdRef.current = session.user.id;
        setUser(session.user);
        setLoading(true);
        loadProfile(session.user.id, 1, session.user);
      } else {
        setLoading(false);
      }
    });
    const { data: { subscription } } = sb.auth.onAuthStateChange((event, session) => {
      // TOKEN_REFRESHED / INITIAL_SESSION / USER_UPDATED — just keep the user ref fresh.
      // Do NOT reset loading or profile: that tears down the whole Router tree and forces
      // DashboardSupplier to unmount + remount, which re-fires every effect (draft restore,
      // verification step, etc.) as if the page was freshly loaded.
      if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED') {
        setUser(session?.user ?? null);
        return;
      }

      setUser(session?.user ?? null);
      if (event === 'SIGNED_OUT' || !session?.user) {
        currentUserIdRef.current = null;
        setProfile(null); setLoading(false);
        clearSwrCache();   // never leak the previous user's cached data to the next
        if (window._profileChannel) { sb.removeChannel(window._profileChannel); window._profileChannel = null; }
        return;
      }
      // Supabase re-fires SIGNED_IN when the tab regains focus (and on cross-tab
      // revalidation) with the SAME user — not just on a fresh login. Doing a full
      // reset there tears the Router tree down and shows the loading screen, so it
      // looks like the whole site reloads every time you switch back. Only reset
      // for a genuinely new/different user; otherwise just keep the user ref fresh.
      if (session.user.id === currentUserIdRef.current) {
        return;
      }
      currentUserIdRef.current = session.user.id;
      // A password login started from the sign-in page loads the profile and
      // redirects on its own. Skip the reset here so we don't wipe that profile
      // and race the redirect (which left the user stuck on the login form on the
      // first attempt). OAuth / cross-tab logins fall through to the normal reset.
      if (window._passwordLoginTs && Date.now() - window._passwordLoginTs < 4000) {
        return;
      }
      setLoading(true);
      setProfile(null);
      setProfileError(false);
      loadProfile(session.user.id, 1, session.user);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Record that this session came from the web, so emails can link to the website
  // rather than the app for this user. Deliberately its own effect keyed on the
  // user id: the auth effect above has five early returns and a comment warning
  // that touching it tears down the Router tree, and this covers every path into a
  // session (initial load, sign-in, cross-tab) without going near that logic.
  // touch_last_platform() skips redundant writes, so firing on every mount is cheap,
  // and a failure here must never surface to the user.
  useEffect(() => {
    if (!user?.id) return;
    sb.rpc('touch_last_platform', { p_platform: 'web' }).then(({ error }) => {
      if (error) console.warn('[last_platform] not recorded:', error.message);
    });
  }, [user?.id]);

  // LIVE SUPPLIER STATUS (2026-10-01). When an admin verifies a supplier, the
  // supplier must see it without reloading the page. REFRESH-ON-FOCUS, not
  // realtime: `profiles` is deliberately not in the supabase_realtime
  // publication (it would broadcast whole profile rows), so the channel opened
  // in loadProfile below never delivers a status change on its own.
  //   - tab becomes visible / window regains focus → re-fetch, at most once per
  //     PROFILE_REFRESH_THROTTLE_MS;
  //   - while the supplier is NOT verified and the tab is visible → also every
  //     PROFILE_POLL_MS (stops when verified, hidden, signed out or unmounted).
  // Suppliers only; traders never run this. Deliberately NOT loadProfile: that
  // path sends emails, writes notifications, toggles the loading screen and
  // re-opens the realtime channel. This is a lean select-and-merge of the
  // admin-written fields only, so it never clobbers an optimistic local edit,
  // never signs anyone out, and never remounts the Router tree.
  const profileRefreshAtRef = useRef(0);
  const profileRefreshInFlightRef = useRef(false);
  const isSupplierProfile = String(profile?.role || '').toLowerCase() === 'supplier';
  const supplierIsVerified = isSupplierProfile
    && getSupplierOnboardingState(profile || {}, user).isVerifiedStatus;
  useEffect(() => {
    const uid = user?.id;
    if (!uid || !isSupplierProfile) return undefined;
    let cancelled = false;

    const refreshProfile = async () => {
      if (profileRefreshInFlightRef.current) return;
      if (Date.now() - profileRefreshAtRef.current < PROFILE_REFRESH_THROTTLE_MS) return;
      profileRefreshInFlightRef.current = true;
      profileRefreshAtRef.current = Date.now();
      try {
        const { data, error } = await sb.from('profiles').select('*').eq('id', uid).maybeSingle();
        if (error) { console.warn('[profile refresh] failed:', error.message); return; }
        if (cancelled || !data) return;
        setProfile(prev => {
          if (!prev || prev.id !== uid) return prev;
          const patch = {};
          LIVE_PROFILE_KEYS.forEach((key) => {
            if (key in data && data[key] !== prev[key]) patch[key] = data[key];
          });
          return Object.keys(patch).length ? { ...prev, ...patch } : prev;
        });
      } finally {
        profileRefreshInFlightRef.current = false;
      }
    };

    let pollId = null;
    const syncPoll = () => {
      const shouldPoll = !supplierIsVerified && document.visibilityState === 'visible';
      if (shouldPoll && !pollId) pollId = setInterval(refreshProfile, PROFILE_POLL_MS);
      if (!shouldPoll && pollId) { clearInterval(pollId); pollId = null; }
    };
    const onFocusOrVisible = () => {
      syncPoll();
      if (document.visibilityState === 'visible') refreshProfile();
    };

    window.addEventListener('focus', onFocusOrVisible);
    document.addEventListener('visibilitychange', onFocusOrVisible);
    syncPoll();
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocusOrVisible);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
      if (pollId) clearInterval(pollId);
    };
  }, [user?.id, isSupplierProfile, supplierIsVerified]);

  useEffect(() => {
    setDisplayCurrency(resolveDisplayCurrency(profile, lang));
  }, [profile, user, lang]);

  const updateDisplayCurrency = async (nextCurrency) => {
    // Only a supplier has a display-currency choice (see resolveDisplayCurrency).
    if (String(profile?.role || '').toLowerCase() !== 'supplier') return;
    const normalized = normalizeDisplayCurrency(nextCurrency || DEFAULT_DISPLAY_CURRENCY);
    setDisplayCurrency(normalized);
    await persistDisplayCurrencyPreference({
      sb,
      userId: user?.id,
      currency: normalized,
      setProfile,
    });
  };

  const maybeNotifyAdminOfConfirmedSupplier = async (profileRow, sessionUser) => {
    if (!shouldNotifyAdminOfConfirmedSupplier(profileRow, sessionUser)) return;
    if (supplierAdminEmailSentRef.current.has(profileRow.id)) return;

    supplierAdminEmailSentRef.current.add(profileRow.id);

    try {
      const { data: existing } = await sb.from('notifications')
        .select('id')
        .eq('user_id', profileRow.id)
        .eq('type', 'supplier_admin_notified')
        .limit(1);

      if (existing?.length) return;

      // MARK BEFORE SEND (2026-09-27). The marker used to be written only after a
      // successful send, so a send that failed left no marker and every later
      // page load tried again — a retry storm. Claim first; if the claim cannot
      // be written, do not send at all. At most one attempt, ever.
      const { error: markError } = await sb.from('notifications').insert({
        user_id: profileRow.id,
        type: 'supplier_admin_notified',
        title_ar: 'تم إشعار الإدارة بطلب المورد بعد تأكيد البريد',
        title_en: 'Admin was notified after supplier email confirmation',
        is_read: true,
      });
      if (markError) {
        console.error('supplier admin notification: could not record the marker, not sending:', markError.message || markError);
        return;
      }

      await sendMaabarEmail({
        type: 'admin_new_supplier',
        data: {
          companyName: profileRow.company_name || profileRow.full_name || '',
          email: profileRow.email || sessionUser?.email || '',
          country: profileRow.country || '',
          city: profileRow.city || '',
          speciality: profileRow.speciality || '',
          whatsapp: profileRow.whatsapp || '',
          wechat: profileRow.wechat || '',
          tradeLink: profileRow.trade_link || '',
          tradeLinks: getSupplierTradeLinks(profileRow),
        },
      });
    } catch (notifyError) {
      // The marker is already written, so a failed send is NOT retried — on
      // this load or any later one. That is the point.
      console.error('supplier admin notification error:', notifyError);
    }
  };

  const loadProfile = async (id, attempt = 1, sessionUser = null) => {
    const { data, error } = await sb.from('profiles').select('*').eq('id', id).single();

    if (!data && attempt < 5) {
      // DB trigger may not have run yet — retry up to 4 times with backoff
      await new Promise(r => setTimeout(r, attempt * 800));
      return loadProfile(id, attempt + 1, sessionUser);
    }

    const profileRow = data;

    if (profileRow) {
      setProfile(profileRow);
      setProfileError(false);
      // Sync language: localStorage is the source of truth for THIS device's UI
      // (see 8466947 — the signup-time 'ar' in the DB used to reset the language on
      // every refresh). It is deliberately NOT written back to profiles.lang:
      // profiles.lang is per-account, localStorage is per-device, so syncing here let
      // whichever device loaded last overwrite a deliberate choice made elsewhere —
      // e.g. the zh a factory owner picks while claiming. Explicit changes still reach
      // the DB through handleSetLang.
      const storedLang = localStorage.getItem('maabar_lang');
      const hasLocalLang = storedLang && ['ar', 'en', 'zh'].includes(storedLang);
      if (!hasLocalLang) {
        if (profileRow.lang && ['ar', 'en', 'zh'].includes(profileRow.lang)) {
          // No localStorage value — restore from DB and persist locally
          setLang(profileRow.lang);
          localStorage.setItem('maabar_lang', profileRow.lang);
        } else {
          // Neither has a value — write current UI lang to DB
          sb.from('profiles').update({ lang }).eq('id', id).then(() => {}).catch(() => {});
        }
      }
      await maybeNotifyAdminOfConfirmedSupplier(profileRow, sessionUser);
      // Send trader welcome email once after email confirmation — deduplicated via ref + notifications table
      if (profileRow?.role === 'buyer' && profileRow?.status === 'active' && !welcomeEmailSentRef.current) {
        welcomeEmailSentRef.current = true;
        try {
          const { data: existing } = await sb.from('notifications')
            .select('id').eq('user_id', id).eq('type', 'trader_welcome_sent').limit(1);
          if (!existing || existing.length === 0) {
            // MARK BEFORE SEND (2026-09-27). This used to insert the marker only
            // after a successful send. send-email 500'd for traders whose email
            // is shared by two profiles, so no marker was ever written and EVERY
            // page load fired another failing call. Claim first; no claim → no
            // send. A welcome email gets at most one attempt, ever.
            const { error: markError } = await sb.from('notifications').insert({
              user_id: id, type: 'trader_welcome_sent',
              title_ar: 'تم إرسال إيميل الترحيب', title_en: 'Welcome email sent',
              is_read: true,
            });
            if (markError) {
              console.error('trader welcome: could not record the marker, not sending:', markError.message || markError);
            } else {
              await sendMaabarEmail({
                type: 'trader_welcome',
                to: profileRow.email,
                // recipientUserId: send-email resolves the profile by ID, not by
                // email — two profiles sharing one email made the email lookup
                // throw ("JSON object requested, multiple (or no) rows returned").
                data: { recipientUserId: id, name: profileRow.full_name || '', lang },
              });
            }
          }
        } catch (e) { console.error('trader welcome error:', e); }
      }
    } else {
      // Failed after all retries — show error screen instead of falling through to buyer dashboard
      console.error('loadProfile failed after retries:', error);
      setProfileError(true);
    }
    setLoading(false);

    if (!profileRow) return; // Don't set up realtime if profile didn't load

    if (window._profileChannel) {
      sb.removeChannel(window._profileChannel);
      window._profileChannel = null;
    }

    // Realtime — لو الأدمن غيّر status المورد يتحدث فوراً
    const ch = sb.channel(`profile-status-${id}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${id}`
      }, (payload) => {
        if (payload.new) setProfile(prev => ({ ...(prev || {}), ...payload.new }));
      })
      .subscribe();
    // نحفظ الـ channel عشان نلغيها لو الجلسة انتهت
    window._profileChannel = ch;
  };

  const handleSetLang = (newLang) => {
    setLang(newLang);
    localStorage.setItem('maabar_lang', newLang);
    if (user?.id) {
      sb.from('profiles').update({ lang: newLang }).eq('id', user.id).then(() => {}).catch(() => {});
    }
  };

  const sharedProps = {
    user,
    profile,
    lang,
    setLang: handleSetLang,
    setUser,
    setProfile,
    displayCurrency,
    setDisplayCurrency: updateDisplayCurrency,
    exchangeRates,
  };

  /* ─── Loading screen ─────────────────────────── */
  if (loading) return <BrandedLoading lang={lang} tone="app" fullscreen />;

  /* ─── App ────────────────────────────────────── */
  return (
    <ErrorBoundary lang={lang}>
      <Router>
        <ClarityGuard>
        <AppContent
          lang={lang}
          profile={profile}
          user={user}
          sharedProps={sharedProps}
          loading={loading}
          profileError={profileError}
          setProfileError={setProfileError}
          setLoading={setLoading}
          loadProfile={loadProfile}
        />
        </ClarityGuard>
      </Router>
    </ErrorBoundary>
  );
}

export default App;